/**
 * Client-side image compression.
 *
 * Why?
 *   Owners upload 8-12 MP photos straight from their phone; those balloon the
 *   `marketplace_items.images` array (data URIs stored inline) and slow the
 *   storefront on 3G. Shrinking to a max longest-edge of 1600px @ JPEG 0.82
 *   keeps the images sharp on retina laptops while typically cutting size by
 *   80–95% for photos taken on modern phones.
 *
 * All work runs on a `<canvas>` so no dependency is added.
 */

const DEFAULTS = {
  maxDimension: 1600, // px — longest edge; both width and height cannot exceed this.
  quality: 0.82,      // JPEG quality (0–1). 0.82 is nearly lossless for product photos.
  mimeType: "image/jpeg",
};

/**
 * Compress a File / Blob into a JPEG data URL.
 *
 * @param {File|Blob} file — the raw upload.
 * @param {object} [opts]  — { maxDimension, quality, mimeType }.
 * @returns {Promise<{ dataUrl: string, width: number, height: number,
 *                     originalBytes: number, compressedBytes: number }>}
 */
export async function compressImage(file, opts = {}) {
  const { maxDimension, quality, mimeType } = { ...DEFAULTS, ...opts };

  // Non-images (PDF etc.) pass through as-is — caller decides what to do.
  if (!file || !file.type || !file.type.startsWith("image/")) {
    const dataUrl = await fileToDataUrl(file);
    return {
      dataUrl,
      width: 0,
      height: 0,
      originalBytes: file?.size || 0,
      compressedBytes: dataUrl.length,
    };
  }

  // Animated GIFs would lose animation on canvas re-encode — bail out.
  if (file.type === "image/gif") {
    const dataUrl = await fileToDataUrl(file);
    return {
      dataUrl,
      width: 0,
      height: 0,
      originalBytes: file.size,
      compressedBytes: dataUrl.length,
    };
  }

  const originalDataUrl = await fileToDataUrl(file);
  const img = await loadImage(originalDataUrl);
  const { width, height } = fitInside(img.naturalWidth, img.naturalHeight, maxDimension);

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  // Fill white so PNG transparency doesn't become black when re-encoded to JPEG.
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, width, height);
  ctx.drawImage(img, 0, 0, width, height);

  const compressedDataUrl = canvas.toDataURL(mimeType, quality);

  // If compression somehow made the file bigger (very small images), keep the original.
  const originalBytes = file.size;
  const compressedBytes = Math.floor((compressedDataUrl.length - "data:image/jpeg;base64,".length) * 0.75);
  if (compressedBytes >= originalBytes && originalBytes < 400_000) {
    return { dataUrl: originalDataUrl, width, height, originalBytes, compressedBytes: originalBytes };
  }
  return { dataUrl: compressedDataUrl, width, height, originalBytes, compressedBytes };
}

/** Percentage savings string, "62%" or "" if no saving. */
export function savingsLabel(originalBytes, compressedBytes) {
  if (!originalBytes || compressedBytes >= originalBytes) return "";
  const pct = Math.round((1 - compressedBytes / originalBytes) * 100);
  return pct > 0 ? `${pct}% smaller` : "";
}

/** Format a byte count as "1.4 MB" / "312 KB". */
export function humanBytes(n) {
  if (!n && n !== 0) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

// ---------- internal helpers ------------------------------------------------

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(reader.error || new Error("read failed"));
    reader.readAsDataURL(file);
  });
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("image decode failed"));
    img.src = src;
  });
}

function fitInside(w, h, max) {
  if (w <= max && h <= max) return { width: w, height: h };
  if (w >= h) {
    return { width: max, height: Math.round((h * max) / w) };
  }
  return { width: Math.round((w * max) / h), height: max };
}
